import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  Bug,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  MessageSquarePlus,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Send,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot, SheetRow } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import {
  api,
  type Ticket,
  type TicketCommentWithUser,
  type TicketPriority,
  type TicketType,
} from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tickets',
  component: TicketsPage,
})

// ── Helpers ──────────────────────────────────────────────────────────────

const TYPE_META: Record<TicketType, { icon: typeof Bug; label: string; color: string }> = {
  bug: { icon: Bug, label: 'Bug', color: 'text-red-400' },
  feature: { icon: Sparkles, label: 'Feature', color: 'text-violet-400' },
  improvement: { icon: Lightbulb, label: 'Verbesserung', color: 'text-amber-400' },
}

const PRIORITY_META: Record<TicketPriority, { label: string; badge: string }> = {
  low: { label: 'Niedrig', badge: 'bg-text-tertiary/15 text-text-tertiary' },
  normal: { label: 'Normal', badge: 'bg-blue-500/15 text-blue-400' },
  high: { label: 'Hoch', badge: 'bg-accent/15 text-accent' },
  critical: { label: 'Kritisch', badge: 'bg-red-500/15 text-red-400' },
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

type SortKey = 'newest' | 'oldest' | 'priority' | 'type'

const SORT_LABELS: Record<SortKey, string> = {
  newest: 'Neueste zuerst',
  oldest: 'Älteste zuerst',
  priority: 'Priorität',
  type: 'Typ',
}

const PRIORITY_RANK: Record<TicketPriority, number> = { critical: 0, high: 1, normal: 2, low: 3 }

const COLLAPSED_KEY = 'tickets-collapsed-columns'

function loadCollapsed(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set()
  }
}

function sortTickets(list: Ticket[], sort: SortKey): Ticket[] {
  const byNewest = (a: Ticket, b: Ticket) => b.createdAt.localeCompare(a.createdAt)
  return [...list].sort((a, b) => {
    switch (sort) {
      case 'oldest':
        return a.createdAt.localeCompare(b.createdAt)
      case 'priority':
        return PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || byNewest(a, b)
      case 'type':
        return a.type.localeCompare(b.type) || byNewest(a, b)
      default:
        return byNewest(a, b)
    }
  })
}

// ── Main Page ────────────────────────────────────────────────────────────

function TicketsPage() {
  const qc = useQueryClient()
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ['tickets'],
    queryFn: () => api.listTickets(),
    enabled: !!me?.user,
    refetchInterval: 30_000,
  })

  // Kanban-Spalten holen damit jedes Ticket sein Status-Badge mit dem
  // tatsächlichen Spaltennamen + Farbe zeigt — dadurch sieht der Ersteller
  // direkt in welcher Phase sein Antrag gerade steht.
  const { data: columnsData } = useQuery({
    queryKey: ['kanban', 'columns'],
    queryFn: () => api.listKanbanColumns(),
    enabled: !!me?.user,
  })
  const columnMap = useMemo(() => {
    const m = new Map<string, { name: string; color: string | null }>()
    for (const c of columnsData?.columns ?? []) {
      m.set(c.id, { name: c.name, color: c.color })
    }
    return m
  }, [columnsData])

  const [createOpen, setCreateOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  // Filter / sort / pagination (all client-side — the data set is small).
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | TicketType>('all')
  const [priorityFilter, setPriorityFilter] = useState<'all' | TicketPriority>('all')
  const [onlyMine, setOnlyMine] = useState(false)
  const [sort, setSort] = useState<SortKey>('newest')
  const [pageSize, setPageSize] = useState(8)
  const [pageByGroup, setPageByGroup] = useState<Record<string, number>>({})
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed)
  // Mobile-Shell (<640): Filter-Sheet hinterm Bottom-Slot (bündelt die
  // Desktop-Controls Suche/Typ/Prio/nur meine/Sort), Titel in der Top-Leiste.
  const [filterSheet, setFilterSheet] = useState(false)
  useMobilePageTitle('Tickets')

  // Persist collapsed columns across reloads.
  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]))
    } catch {
      // ignore
    }
  }, [collapsed])

  // Reset per-group pagination whenever the visible set changes (filter / sort /
  // page size) so the user lands on the top of the fresh result, not a stale
  // last page. Also keeps pageByGroup from accumulating stale ids.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset is keyed on the filter/sort inputs, not pageByGroup itself
  useEffect(() => {
    setPageByGroup({})
  }, [search, typeFilter, priorityFilter, onlyMine, sort, pageSize])

  if (meLoading) return null
  if (!me?.user) return <Navigate to="/login" />

  const tickets = ticketsData?.tickets ?? []
  // "Board access" = legacy admin, or a role with the all/manageTickets
  // permission. Such users may edit/delete/move ANY ticket.
  const perms = me.user.roleObj?.permissions
  const canManageBoard =
    me.user.role === 'admin' || !!me.user.roleObj?.isAdmin || !!perms?.all || !!perms?.manageTickets

  const myId = me.user.id
  // Filter, then sort.
  const q = search.trim().toLowerCase()
  const hasActiveFilter = q !== '' || typeFilter !== 'all' || priorityFilter !== 'all' || onlyMine
  const filtered = tickets.filter((t) => {
    if (typeFilter !== 'all' && t.type !== typeFilter) return false
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false
    if (onlyMine && t.createdBy !== myId) return false
    if (q && !t.title.toLowerCase().includes(q) && !t.description.toLowerCase().includes(q))
      return false
    return true
  })
  const sorted = sortTickets(filtered, sort)

  // Group filtered+sorted tickets by kanban column, in admin column order
  // (position). Column discovery uses ALL tickets so the order stays stable
  // regardless of the active filter; orphan columns append under their raw id
  // ('inbox' → "Eingang"). Empty groups are hidden.
  const orderedColumns = [...(columnsData?.columns ?? [])].sort((a, b) => a.position - b.position)
  const groupDefs: { id: string; name: string; color: string | null }[] = orderedColumns.map(
    (c) => ({ id: c.id, name: c.name, color: c.color }),
  )
  const knownColumnIds = new Set(orderedColumns.map((c) => c.id))
  for (const t of tickets) {
    if (!knownColumnIds.has(t.columnId)) {
      knownColumnIds.add(t.columnId)
      groupDefs.push({
        id: t.columnId,
        name: t.columnId === 'inbox' ? 'Eingang' : t.columnId,
        color: null,
      })
    }
  }
  const groups = groupDefs
    .map((g) => ({ ...g, items: sorted.filter((t) => t.columnId === g.id) }))
    .filter((g) => g.items.length > 0)

  return (
    <section className="animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Filter → Sheet + ＋ Neues Ticket. */}
      <MobileBottomSlots>
        <BarSlot
          label="Filter"
          icon={<SlidersHorizontal className="h-5 w-5" />}
          active={hasActiveFilter}
          ariaLabel="Tickets filtern und sortieren"
          ariaHasPopup
          onClick={() => setFilterSheet(true)}
        />
        <BarAccentPlus ariaLabel="Neues Ticket" onClick={() => setCreateOpen(true)} />
      </MobileBottomSlots>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Tickets"
        actions={
          <Button onClick={() => setCreateOpen(true)} variant="primary">
            <Plus className="h-4 w-4" />
            Neues Ticket
          </Button>
        }
      />

      {/* Filter-Sheet (<640): dieselben States wie die Desktop-Controls —
          SheetRow-Taps lassen das Sheet offen (Mehrfach-Justage), schließen
          per Backdrop/X. */}
      <SidePanel open={filterSheet} onClose={() => setFilterSheet(false)} title="Filter">
        <div className="space-y-5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tickets durchsuchen …"
              className="pl-8"
            />
          </div>
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wider text-text-tertiary">Typ</p>
            <div className="flex flex-col gap-1">
              <SheetRow
                active={typeFilter === 'all'}
                label="Alle Typen"
                onClick={() => setTypeFilter('all')}
              />
              {(Object.entries(TYPE_META) as [TicketType, (typeof TYPE_META)[TicketType]][]).map(
                ([k, v]) => (
                  <SheetRow
                    key={k}
                    active={typeFilter === k}
                    label={v.label}
                    onClick={() => setTypeFilter(k)}
                  />
                ),
              )}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wider text-text-tertiary">Priorität</p>
            <div className="flex flex-col gap-1">
              <SheetRow
                active={priorityFilter === 'all'}
                label="Alle Prioritäten"
                onClick={() => setPriorityFilter('all')}
              />
              {(
                Object.entries(PRIORITY_META) as [
                  TicketPriority,
                  (typeof PRIORITY_META)[TicketPriority],
                ][]
              ).map(([k, v]) => (
                <SheetRow
                  key={k}
                  active={priorityFilter === k}
                  label={v.label}
                  onClick={() => setPriorityFilter(k)}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wider text-text-tertiary">Anzeige</p>
            <SheetRow active={onlyMine} label="Nur meine" onClick={() => setOnlyMine(!onlyMine)} />
          </div>
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wider text-text-tertiary">Sortierung</p>
            <div className="flex flex-col gap-1">
              {(Object.entries(SORT_LABELS) as [SortKey, string][]).map(([k, l]) => (
                <SheetRow key={k} active={sort === k} label={l} onClick={() => setSort(k)} />
              ))}
            </div>
          </div>
        </div>
      </SidePanel>

      {isLoading ? (
        <p className="text-sm text-text-tertiary py-8 text-center">Lade Tickets …</p>
      ) : tickets.length === 0 ? (
        <EmptyState
          icon={MessageSquarePlus}
          message="Noch keine Tickets erstellt."
          action={
            <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Neues Ticket
            </Button>
          }
        />
      ) : (
        <div className="space-y-4 pt-4">
          <TicketControls
            search={search}
            setSearch={setSearch}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            priorityFilter={priorityFilter}
            setPriorityFilter={setPriorityFilter}
            onlyMine={onlyMine}
            setOnlyMine={setOnlyMine}
            sort={sort}
            setSort={setSort}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
          {groups.length === 0 ? (
            <EmptyState
              icon={Search}
              message={
                hasActiveFilter
                  ? 'Keine Tickets entsprechen den Filtern.'
                  : 'Noch keine Tickets erstellt.'
              }
            />
          ) : (
            <div className="space-y-5">
              {groups.map((g) => (
                <TicketGroup
                  key={g.id}
                  group={g}
                  collapsed={collapsed.has(g.id)}
                  onToggle={() =>
                    setCollapsed((prev) => {
                      const next = new Set(prev)
                      if (next.has(g.id)) next.delete(g.id)
                      else next.add(g.id)
                      return next
                    })
                  }
                  page={pageByGroup[g.id] ?? 1}
                  pageSize={pageSize}
                  onPage={(p) => setPageByGroup((prev) => ({ ...prev, [g.id]: p }))}
                  columnMap={columnMap}
                  myId={myId}
                  onOpen={setDetailId}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <CreateTicketPanel
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['tickets'] })
            setCreateOpen(false)
            setDetailId(id)
          }}
        />
      )}

      {detailId && (
        <TicketDetailPanel
          ticketId={detailId}
          myId={me.user.id}
          canManageBoard={canManageBoard}
          onClose={() => setDetailId(null)}
          onDeleted={() => {
            qc.invalidateQueries({ queryKey: ['tickets'] })
            setDetailId(null)
          }}
        />
      )}
    </section>
  )
}

// ── Controls (search / filter / sort / page size) ─────────────────────────

function TicketControls({
  search,
  setSearch,
  typeFilter,
  setTypeFilter,
  priorityFilter,
  setPriorityFilter,
  onlyMine,
  setOnlyMine,
  sort,
  setSort,
  pageSize,
  setPageSize,
}: {
  search: string
  setSearch: (v: string) => void
  typeFilter: 'all' | TicketType
  setTypeFilter: (v: 'all' | TicketType) => void
  priorityFilter: 'all' | TicketPriority
  setPriorityFilter: (v: 'all' | TicketPriority) => void
  onlyMine: boolean
  setOnlyMine: (v: boolean) => void
  sort: SortKey
  setSort: (v: SortKey) => void
  pageSize: number
  setPageSize: (v: number) => void
}) {
  return (
    // <640 übernimmt das Filter-Sheet der Bottom-Bar diese Controls —
    // phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab).
    <div className="flex phone:hidden flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[180px]">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Tickets durchsuchen …"
          className="pl-8"
        />
      </div>
      <Select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value as 'all' | TicketType)}
        className="w-auto"
      >
        <option value="all">Alle Typen</option>
        {Object.entries(TYPE_META).map(([k, v]) => (
          <option key={k} value={k}>
            {v.label}
          </option>
        ))}
      </Select>
      <Select
        value={priorityFilter}
        onChange={(e) => setPriorityFilter(e.target.value as 'all' | TicketPriority)}
        className="w-auto"
      >
        <option value="all">Alle Prioritäten</option>
        {Object.entries(PRIORITY_META).map(([k, v]) => (
          <option key={k} value={k}>
            {v.label}
          </option>
        ))}
      </Select>
      <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className="w-auto">
        {Object.entries(SORT_LABELS).map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </Select>
      <button
        type="button"
        onClick={() => setOnlyMine(!onlyMine)}
        aria-pressed={onlyMine}
        className={cn(
          'h-9 rounded-lg border px-3 text-xs font-medium transition-colors touch:opacity-100',
          onlyMine
            ? 'border-accent/60 bg-accent/15 text-accent'
            : 'border-border-default bg-bg-overlay/50 text-text-secondary hover:text-text-primary',
        )}
      >
        Nur meine
      </button>
      <Select
        value={String(pageSize)}
        onChange={(e) => setPageSize(Number(e.target.value))}
        className="w-auto"
        aria-label="Pro Seite"
      >
        {[8, 15, 30].map((n) => (
          <option key={n} value={n}>
            {n}/Seite
          </option>
        ))}
      </Select>
    </div>
  )
}

// ── Ticket Group (collapsible + paginated) ────────────────────────────────

function TicketGroup({
  group,
  collapsed,
  onToggle,
  page,
  pageSize,
  onPage,
  columnMap,
  myId,
  onOpen,
}: {
  group: { id: string; name: string; color: string | null; items: Ticket[] }
  collapsed: boolean
  onToggle: () => void
  page: number
  pageSize: number
  onPage: (p: number) => void
  columnMap: Map<string, { name: string; color: string | null }>
  myId: string
  onOpen: (id: string) => void
}) {
  const totalPages = Math.max(1, Math.ceil(group.items.length / pageSize))
  const clampedPage = Math.min(Math.max(1, page), totalPages)
  const visible = group.items.slice((clampedPage - 1) * pageSize, clampedPage * pageSize)

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex w-full items-center gap-2 px-1 pb-1.5 text-left touch:opacity-100"
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-text-tertiary transition-transform',
            collapsed && '-rotate-90',
          )}
        />
        <span
          className="h-2.5 w-2.5 rounded-full shrink-0"
          style={{ backgroundColor: group.color ?? '#6B7280' }}
        />
        <h2 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
          {group.name}
        </h2>
        <span className="text-2xs tabular-nums text-text-tertiary">{group.items.length}</span>
      </button>
      {!collapsed && (
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
          <ul className="divide-y divide-border-subtle">
            {visible.map((t) => (
              <li key={t.id}>
                <TicketRow
                  ticket={t}
                  column={columnMap.get(t.columnId) ?? null}
                  isMine={t.createdBy === myId}
                  onClick={() => onOpen(t.id)}
                />
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-3 py-2 text-xs text-text-tertiary">
              <button
                type="button"
                disabled={clampedPage <= 1}
                onClick={() => onPage(clampedPage - 1)}
                aria-label="Vorherige Seite"
                className="rounded p-1 hover:bg-bg-overlay/60 disabled:pointer-events-none disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="tabular-nums">
                {clampedPage} / {totalPages}
              </span>
              <button
                type="button"
                disabled={clampedPage >= totalPages}
                onClick={() => onPage(clampedPage + 1)}
                aria-label="Nächste Seite"
                className="rounded p-1 hover:bg-bg-overlay/60 disabled:pointer-events-none disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Ticket Row ───────────────────────────────────────────────────────────

function TicketRow({
  ticket: t,
  column,
  isMine,
  onClick,
}: {
  ticket: Ticket
  column: { name: string; color: string | null } | null
  isMine: boolean
  onClick: () => void
}) {
  const tm = TYPE_META[t.type as TicketType] ?? TYPE_META.bug
  const pm = PRIORITY_META[t.priority as TicketPriority] ?? PRIORITY_META.normal
  const Icon = tm.icon

  // Spalten-Badge mit Live-Farbe — der Ersteller sieht so direkt in welcher
  // Phase sein Ticket gerade steckt. Fallback nur, wenn die Spalten-Liste
  // noch lädt oder die Spalte gelöscht wurde.
  const colName = column?.name ?? (t.columnId === 'inbox' ? 'Eingang' : t.columnId)
  const colColor = column?.color ?? '#6B7280'

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-4 py-3 hover:bg-bg-overlay/40 transition-colors flex items-center gap-3 touch:opacity-100"
    >
      <Icon className={cn('h-4.5 w-4.5 shrink-0', tm.color)} strokeWidth={1.8} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary truncate">{t.title}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className={cn('text-2xs px-1.5 py-0.5 rounded-full font-medium', pm.badge)}>
            {pm.label}
          </span>
          <span className="text-2xs text-text-tertiary tabular-nums">
            {formatDate(t.createdAt)}
          </span>
          <span
            className="text-2xs px-1.5 py-0.5 rounded-full font-medium border"
            style={{
              backgroundColor: `${colColor}26`,
              borderColor: `${colColor}66`,
              color: colColor,
            }}
            title={`Status: ${colName}`}
          >
            {colName}
          </span>
          <span className="text-2xs text-text-tertiary truncate">
            {isMine ? 'von dir' : `von ${t.creatorName ?? '—'}`}
          </span>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-text-tertiary shrink-0" />
    </button>
  )
}

// ── Create Ticket Panel ──────────────────────────────────────────────────

function CreateTicketPanel({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<TicketType>('bug')
  const [priority, setPriority] = useState<TicketPriority>('normal')
  const [files, setFiles] = useState<File[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const create = useMutation({
    mutationFn: async () => {
      const { ticket } = await api.createTicket({ title, description, type, priority })
      // Upload attachments
      for (const f of files) {
        await api.uploadTicketAttachment(ticket.id, f)
      }
      return ticket
    },
    onSuccess: (ticket) => {
      notify.success('Ticket erstellt')
      onCreated(ticket.id)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const dropped = Array.from(e.dataTransfer.files)
    setFiles((prev) => [...prev, ...dropped])
  }, [])

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Neues Ticket"
      description="Bug melden, Feature anfragen oder eine Verbesserung vorschlagen."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button
            disabled={!title.trim() || !description.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Ticket erstellen
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="t-title">Titel</Label>
          <Input
            id="t-title"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 500))}
            placeholder="Kurze Beschreibung des Problems / Wunsches"
            autoFocus
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="t-type">Typ</Label>
            <Select
              id="t-type"
              value={type}
              onChange={(e) => setType(e.target.value as TicketType)}
            >
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
              <option value="improvement">Verbesserung</option>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="t-priority">Priorität</Label>
            <Select
              id="t-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
            >
              <option value="low">Niedrig</option>
              <option value="normal">Normal</option>
              <option value="high">Hoch</option>
              <option value="critical">Kritisch</option>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="t-desc">Beschreibung</Label>
          <textarea
            id="t-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 10000))}
            rows={5}
            placeholder="Was ist passiert? Was wurde erwartet? Schritte zum Reproduzieren …"
            className="flex w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3.5 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-y"
          />
        </div>

        {/* File upload */}
        <div className="space-y-2">
          <Label>Anhänge</Label>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: drop target */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="border-2 border-dashed border-border-default rounded-lg p-4 text-center hover:border-accent/40 transition-colors cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click()
            }}
          >
            <Upload className="h-6 w-6 mx-auto text-text-tertiary mb-1" strokeWidth={1.5} />
            <p className="text-xs text-text-tertiary">
              Dateien hierhin ziehen oder klicken (max 10 MB)
            </p>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? [])
                setFiles((prev) => [...prev, ...picked])
                e.target.value = ''
              }}
            />
          </div>
          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${f.size}-${f.lastModified}`}
                  className="flex items-center gap-2 text-xs text-text-secondary"
                >
                  <Paperclip className="h-3 w-3 shrink-0" />
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="tabular-nums text-text-tertiary shrink-0">
                    {(f.size / 1024).toFixed(0)} KB
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setFiles((prev) => prev.filter((_, j) => j !== i))
                    }}
                    className="p-0.5 hover:text-red-400 touch:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SidePanel>
  )
}

// ── Ticket Detail Panel ──────────────────────────────────────────────────

function TicketDetailPanel({
  ticketId,
  myId,
  canManageBoard,
  onClose,
  onDeleted,
}: {
  ticketId: string
  myId: string
  canManageBoard: boolean
  onClose: () => void
  onDeleted: () => void
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => api.getTicket(ticketId),
    refetchInterval: 15_000,
  })

  // Spalten holen damit auch hier das aktuelle Status-Badge mit Live-Farbe
  // angezeigt werden kann.
  const { data: columnsData } = useQuery({
    queryKey: ['kanban', 'columns'],
    queryFn: () => api.listKanbanColumns(),
  })

  const deleteMut = useMutation({
    mutationFn: () => api.deleteTicket(ticketId),
    onSuccess: () => {
      notify.success('Ticket gelöscht')
      onDeleted()
    },
    onError: (err: Error) => notify.error(err.message),
  })
  const [editing, setEditing] = useState(false)

  if (isLoading || !data) {
    return (
      <SidePanel open onClose={onClose} title="Ticket">
        <p className="text-sm text-text-tertiary py-4 text-center">Lade …</p>
      </SidePanel>
    )
  }

  const { ticket, attachments, comments, creatorName, assigneeName } = data
  const tm = TYPE_META[ticket.type as TicketType] ?? TYPE_META.bug
  const pm = PRIORITY_META[ticket.priority as TicketPriority] ?? PRIORITY_META.normal
  const Icon = tm.icon
  const isAccepted = ticket.status === 'accepted' || ticket.columnId !== 'inbox'
  const isOwn = ticket.createdBy === myId
  // Creator, admin and board managers may edit the content; only board managers
  // (or the creator while still unaccepted) may delete.
  const canEdit = canManageBoard || isOwn
  const canDelete = canManageBoard || (isOwn && !isAccepted)
  const column = (columnsData?.columns ?? []).find((c) => c.id === ticket.columnId) ?? null
  const colName = column?.name ?? (ticket.columnId === 'inbox' ? 'Eingang' : ticket.columnId)
  const colColor = column?.color ?? '#6B7280'

  return (
    <SidePanel
      open
      onClose={onClose}
      title={ticket.title}
      description={`${tm.label} · ${pm.label} · ${formatDate(ticket.createdAt)}`}
      footer={
        canDelete ? (
          <Button
            variant="ghost"
            className="text-red-400 hover:text-red-300"
            onClick={() => {
              if (confirm('Ticket wirklich löschen?')) deleteMut.mutate()
            }}
          >
            <Trash2 className="h-4 w-4" /> Löschen
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-5 pb-2">
        {editing ? (
          <TicketEditForm ticket={ticket} onDone={() => setEditing(false)} />
        ) : (
          <>
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('inline-flex items-center gap-1 text-xs font-medium', tm.color)}>
                <Icon className="h-3.5 w-3.5" /> {tm.label}
              </span>
              <span className={cn('text-2xs px-1.5 py-0.5 rounded-full font-medium', pm.badge)}>
                {pm.label}
              </span>
              <span
                className="text-2xs px-2 py-0.5 rounded-full font-medium border"
                style={{
                  backgroundColor: `${colColor}26`,
                  borderColor: `${colColor}66`,
                  color: colColor,
                }}
                title={`Status: ${colName}`}
              >
                {colName}
              </span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="ml-auto inline-flex items-center gap-1 text-[11px] text-text-tertiary hover:text-accent touch:opacity-100"
                >
                  <Pencil className="h-3 w-3" /> Bearbeiten
                </button>
              )}
            </div>

            {/* Creator/assignee */}
            <div className="text-xs text-text-tertiary space-y-0.5">
              <p>
                Erstellt von:{' '}
                <span className="text-text-secondary">{creatorName ?? ticket.createdBy}</span>
              </p>
              {assigneeName && (
                <p>
                  Zugewiesen an: <span className="text-text-secondary">{assigneeName}</span>
                </p>
              )}
            </div>

            {/* Description */}
            <div className="text-sm text-text-primary whitespace-pre-wrap break-words bg-bg-overlay/30 rounded-lg p-3 border border-border-subtle">
              {ticket.description}
            </div>
          </>
        )}

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Anhänge ({attachments.length})
            </p>
            <ul className="space-y-1">
              {attachments.map((att) => (
                <li key={att.id} className="flex items-center gap-2 text-xs">
                  <Paperclip className="h-3 w-3 text-text-tertiary shrink-0" />
                  <a
                    href={api.ticketAttachmentUrl(ticketId, att.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline truncate flex-1"
                  >
                    {att.filename}
                  </a>
                  <span className="text-text-tertiary tabular-nums shrink-0">
                    {(att.sizeBytes / 1024).toFixed(0)} KB
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Comments */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
            Kommentare ({comments.length})
          </p>
          {comments.length === 0 ? (
            <p className="text-xs text-text-tertiary">Noch keine Kommentare.</p>
          ) : (
            <ul className="space-y-2">
              {comments.map((c) => (
                <CommentItem
                  key={c.id}
                  comment={c}
                  canDelete={canManageBoard || c.userId === myId}
                  ticketId={ticketId}
                />
              ))}
            </ul>
          )}
          <AddCommentForm ticketId={ticketId} />
        </div>
      </div>
    </SidePanel>
  )
}

// Inline editor for a ticket's content (title/type/priority/description).
// Shown to the creator and to admins/board managers; the backend enforces who
// may actually save (creator → content only; board → everything).
function TicketEditForm({ ticket, onDone }: { ticket: Ticket; onDone: () => void }) {
  const qc = useQueryClient()
  const [title, setTitle] = useState(ticket.title)
  const [type, setType] = useState<TicketType>(ticket.type as TicketType)
  const [priority, setPriority] = useState<TicketPriority>(ticket.priority as TicketPriority)
  const [description, setDescription] = useState(ticket.description)
  const valid = title.trim().length > 0 && description.trim().length > 0
  const save = useMutation({
    mutationFn: () =>
      api.updateTicket(ticket.id, {
        title: title.trim(),
        description: description.trim(),
        type,
        priority,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticket.id] })
      qc.invalidateQueries({ queryKey: ['tickets'] })
      notify.success('Ticket aktualisiert')
      onDone()
    },
    onError: (err: Error) => notify.error(err.message),
  })
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="e-title">Titel</Label>
        <Input
          id="e-title"
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 500))}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor="e-type">Typ</Label>
          <Select id="e-type" value={type} onChange={(e) => setType(e.target.value as TicketType)}>
            {Object.entries(TYPE_META).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="e-prio">Priorität</Label>
          <Select
            id="e-prio"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
          >
            {Object.entries(PRIORITY_META).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="e-desc">Beschreibung</Label>
        <textarea
          id="e-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 10000))}
          rows={5}
          className="flex w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3.5 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-y"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Abbrechen
        </Button>
        <Button
          size="sm"
          disabled={!valid || save.isPending}
          onClick={() => valid && save.mutate()}
        >
          Speichern
        </Button>
      </div>
    </div>
  )
}

function CommentItem({
  comment,
  canDelete,
  ticketId,
}: {
  comment: TicketCommentWithUser
  canDelete: boolean
  ticketId: string
}) {
  const qc = useQueryClient()
  const del = useMutation({
    mutationFn: () => api.deleteTicketComment(ticketId, comment.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket', ticketId] }),
  })

  return (
    <div className="bg-bg-overlay/40 rounded-lg p-2.5 border border-border-subtle">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-xs font-medium text-text-secondary">
          {comment.userName ?? 'Unbekannt'}
        </span>
        <div className="flex items-center gap-1">
          <span className="text-2xs text-text-tertiary tabular-nums">
            {formatDate(comment.createdAt)}
          </span>
          {canDelete && (
            <button
              type="button"
              onClick={() => del.mutate()}
              className="p-0.5 text-text-tertiary hover:text-red-400 touch:opacity-100"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-text-primary whitespace-pre-wrap break-words">{comment.content}</p>
    </div>
  )
}

function AddCommentForm({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient()
  const [content, setContent] = useState('')
  const add = useMutation({
    mutationFn: () => api.addTicketComment(ticketId, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] })
      setContent('')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!content.trim() || add.isPending) return
    add.mutate()
  }

  return (
    <form onSubmit={onSubmit} className="flex items-end gap-2 mt-2">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value.slice(0, 5000))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            onSubmit(e as unknown as FormEvent)
          }
        }}
        rows={2}
        placeholder="Kommentar schreiben …"
        className="flex-1 rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-none"
      />
      <Button type="submit" disabled={!content.trim() || add.isPending} size="sm">
        <Send className="h-3.5 w-3.5" />
      </Button>
    </form>
  )
}
